import { useEffect, useState } from "react";
import { Box, Button, Layer, Text, TextInput } from "grommet";
import { Search } from "grommet-icons";
import { craneDisplayValue, searchCraneGuageSessions } from "./craneSessions";

export default function CraneLinkModal({ onClose, onLink, loadSessions }) {
  const [sessions, setSessions] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [linkError, setLinkError] = useState("");
  const [linkingId, setLinkingId] = useState("");

  useEffect(() => {
    let cancelled = false;

    loadSessions()
      .then((items) => {
        if (!cancelled) setSessions(items);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          setLoadError("Unable to load Crane sessions.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loadSessions]);

  const runSearch = () => {
    setLinkError("");
    setResults(searchCraneGuageSessions(sessions, query));
  };

  const handleLink = async (session) => {
    if (linkingId) return;
    setLinkingId(session.session_id);
    setLinkError("");

    try {
      await onLink(session);
    } catch (error) {
      console.error(error);
      setLinkError("Unable to link this Crane session.");
      setLinkingId("");
    }
  };

  return (
    <Layer
      position="center"
      modal
      responsive={false}
      onEsc={linkingId ? undefined : onClose}
      onClickOutside={linkingId ? undefined : onClose}
    >
      <Box width={{ width: "xlarge", max: "90vw" }} pad="medium" gap="medium">
        <Box direction="row" justify="between" align="center">
          <Text weight="bold" size="large">
            Link Crane session
          </Text>
          <Button label="Close" onClick={onClose} disabled={Boolean(linkingId)} />
        </Box>

        <Box direction="row" gap="small" align="center">
          <TextInput
            aria-label="Search Crane sessions"
            placeholder="Search by name or email"
            value={query}
            icon={<Search />}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                runSearch();
              }
            }}
          />
          <Button
            label="Search"
            primary
            color="black"
            onClick={runSearch}
            disabled={loading || Boolean(loadError)}
          />
        </Box>

        {loading && <Text>Loading Crane sessions...</Text>}
        {loadError && (
          <Text color="status-critical" role="alert">
            {loadError}
          </Text>
        )}
        {linkError && (
          <Text color="status-critical" role="alert">
            {linkError}
          </Text>
        )}

        <Box
          flex={false}
          overflow="auto"
          gap="small"
          height={{ max: "60vh" }}
          fill="horizontal"
        >
          {!loading && !loadError && results === null && (
            <Text color="dark-4">Search by name or email.</Text>
          )}
          {results?.length === 0 && (
            <Text>
              No Crane sessions found for "{query.trim()}".
            </Text>
          )}
          {results?.map((session) => {
            const name = craneDisplayValue(session.user_name) || "Unnamed tester";
            const email = craneDisplayValue(session.user_email);
            const testName = craneDisplayValue(session.test_name);

            return (
              <Box
                key={session.session_id}
                flex={false}
                pad="small"
                gap="medium"
                background="light-1"
                round="xsmall"
                direction="row"
                justify="between"
                align="center"
                fill="horizontal"
              >
                <Box flex="grow" gap="xxsmall" width={{ min: "small" }}>
                  <Text weight="bold">{name}</Text>
                  {email && <Text size="small">{email}</Text>}
                  {testName && <Text size="small">{testName}</Text>}
                </Box>
                <Box flex={false}>
                  <Button
                    label={
                      linkingId === session.session_id
                        ? "Linking..."
                        : "Link to PodSession"
                    }
                    primary
                    color="black"
                    disabled={Boolean(linkingId)}
                    onClick={() => handleLink(session)}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Layer>
  );
}
